<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { X, Plus, Trash2 } from 'lucide-vue-next'
import { AppButton } from '@/design-system/components'
import { lessonsApi } from '@/api'
import { countOccurrences, todayKey, dayOfWeekOf, toLocalDateKey, addDays } from '@/utils/date'

/**
 * Add-lesson form on the slot API (server 1.13.0). One-off → POST /lessons;
 * repeats-weekly → POST /lessons/slots with a live "{N} lessons" count.
 */
const props = defineProps<{ modelValue: boolean; classId: string }>()
const emit = defineEmits<{ 'update:modelValue': [value: boolean]; created: [] }>()

type Cadence = 'once' | 'weekly'
type WeeklyTime = { dayOfWeek: string; startTime: string; endTime: string; room: string }

const DAYS = [
  { value: 'MONDAY', label: 'Monday' },
  { value: 'TUESDAY', label: 'Tuesday' },
  { value: 'WEDNESDAY', label: 'Wednesday' },
  { value: 'THURSDAY', label: 'Thursday' },
  { value: 'FRIDAY', label: 'Friday' },
  { value: 'SATURDAY', label: 'Saturday' },
  { value: 'SUNDAY', label: 'Sunday' },
]
function todayDow(): string {
  return dayOfWeekOf(todayKey())
}

const cadence = ref<Cadence>('once')
const title = ref('')
const saving = ref(false)
const error = ref<string | null>(null)

// One-off
const date = ref(todayKey())
const startTime = ref('09:00')
const endTime = ref('10:00')
const room = ref('')

// Weekly
const seriesStart = ref(todayKey())
const seriesEnd = ref(toLocalDateKey(addDays(new Date(), 12 * 7))) // ~a term
const weeklyTimes = ref<WeeklyTime[]>([
  { dayOfWeek: todayDow(), startTime: '09:00', endTime: '10:00', room: '' },
])

function addWeeklyTime() {
  const last = weeklyTimes.value[weeklyTimes.value.length - 1]
  weeklyTimes.value.push({
    dayOfWeek: last?.dayOfWeek ?? todayDow(),
    startTime: last?.startTime ?? '09:00',
    endTime: last?.endTime ?? '10:00',
    room: '',
  })
}
function removeWeeklyTime(i: number) {
  weeklyTimes.value.splice(i, 1)
}

const lessonCount = computed(() =>
  weeklyTimes.value.reduce((sum, w) => sum + countOccurrences(w.dayOfWeek, seriesStart.value, seriesEnd.value), 0),
)

const canSave = computed(() => {
  if (saving.value) return false
  if (cadence.value === 'once') return !!date.value && !!startTime.value && !!endTime.value
  return !!seriesStart.value && !!seriesEnd.value && weeklyTimes.value.length > 0 && lessonCount.value > 0
})

function close() {
  emit('update:modelValue', false)
}
function reset() {
  cadence.value = 'once'
  title.value = ''
  error.value = null
  date.value = todayKey()
  startTime.value = '09:00'
  endTime.value = '10:00'
  room.value = ''
  seriesStart.value = todayKey()
  seriesEnd.value = toLocalDateKey(addDays(new Date(), 12 * 7))
  weeklyTimes.value = [{ dayOfWeek: todayDow(), startTime: '09:00', endTime: '10:00', room: '' }]
}
watch(() => props.modelValue, (open) => { if (open) reset() })

async function save() {
  if (!canSave.value) return
  saving.value = true
  error.value = null
  try {
    if (cadence.value === 'once') {
      await lessonsApi.createLesson({
        classId: props.classId,
        startTime: `${date.value}T${startTime.value}:00`,
        endTime: `${date.value}T${endTime.value}:00`,
        room: room.value.trim() || undefined,
        title: title.value.trim() || undefined,
      })
    } else {
      await lessonsApi.createLessonSlots({
        classId: props.classId,
        seriesStart: seriesStart.value,
        seriesEnd: seriesEnd.value,
        slots: weeklyTimes.value.map((w) => ({
          dayOfWeek: w.dayOfWeek,
          startTime: w.startTime,
          endTime: w.endTime,
          room: w.room.trim() || undefined,
          title: title.value.trim() || undefined,
        })),
      })
    }
    emit('created')
    close()
  } catch (err) {
    console.error('Failed to create lesson(s):', err)
    error.value = 'Could not create the lesson. Please try again.'
  } finally {
    saving.value = false
  }
}
</script>

<template>
  <Teleport to="body">
    <div v-if="modelValue" class="alm">
      <div class="alm__backdrop" @click="close" />
      <div class="alm__panel">
        <header class="alm__head">
          <h2 class="alm__title">Add lesson</h2>
          <button type="button" class="alm__x" aria-label="Close" @click="close"><X :size="18" /></button>
        </header>

        <div class="alm__body">
          <!-- cadence toggle -->
          <div class="alm__seg">
            <button type="button" class="alm__segbtn" :class="{ 'alm__segbtn--on': cadence === 'once' }" @click="cadence = 'once'">One-off</button>
            <button type="button" class="alm__segbtn" :class="{ 'alm__segbtn--on': cadence === 'weekly' }" @click="cadence = 'weekly'">Repeats weekly</button>
          </div>

          <label class="alm__label">Title <span class="alm__opt">(optional)</span></label>
          <input v-model="title" type="text" class="alm__input" placeholder="e.g. Algebra revision" />

          <!-- One-off -->
          <template v-if="cadence === 'once'">
            <label class="alm__label">Date</label>
            <input v-model="date" type="date" class="alm__input" />
            <div class="alm__grid2">
              <div>
                <label class="alm__label">Start</label>
                <input v-model="startTime" type="time" class="alm__input" />
              </div>
              <div>
                <label class="alm__label">End</label>
                <input v-model="endTime" type="time" class="alm__input" />
              </div>
            </div>
            <label class="alm__label">Room <span class="alm__opt">(optional)</span></label>
            <input v-model="room" type="text" class="alm__input" placeholder="e.g. B102" />
          </template>

          <!-- Weekly -->
          <template v-else>
            <div class="alm__grid2">
              <div>
                <label class="alm__label">From</label>
                <input v-model="seriesStart" type="date" class="alm__input" />
              </div>
              <div>
                <label class="alm__label">Until</label>
                <input v-model="seriesEnd" type="date" class="alm__input" />
              </div>
            </div>

            <label class="alm__label">Weekly times</label>
            <div v-for="(w, i) in weeklyTimes" :key="i" class="alm__wt">
              <select v-model="w.dayOfWeek" class="alm__input alm__wt-day">
                <option v-for="d in DAYS" :key="d.value" :value="d.value">{{ d.label }}</option>
              </select>
              <input v-model="w.startTime" type="time" class="alm__input alm__wt-time" />
              <input v-model="w.endTime" type="time" class="alm__input alm__wt-time" />
              <input v-model="w.room" type="text" class="alm__input alm__wt-room" placeholder="Room" />
              <button
                type="button"
                class="alm__wt-del"
                :disabled="weeklyTimes.length === 1"
                aria-label="Remove time"
                @click="removeWeeklyTime(i)"
              >
                <Trash2 :size="15" />
              </button>
            </div>
            <button type="button" class="alm__add" @click="addWeeklyTime"><Plus :size="14" /> Add another time</button>

            <div class="alm__count">
              <b>{{ lessonCount }}</b> lesson{{ lessonCount === 1 ? '' : 's' }} will be created
            </div>
          </template>

          <p v-if="error" class="alm__error">{{ error }}</p>
        </div>

        <footer class="alm__foot">
          <AppButton variant="ghost" size="md" @click="close">Cancel</AppButton>
          <AppButton variant="primary" size="md" :loading="saving" :disabled="!canSave" @click="save">
            {{ cadence === 'weekly' ? `Create ${lessonCount} lesson${lessonCount === 1 ? '' : 's'}` : 'Add lesson' }}
          </AppButton>
        </footer>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.alm {
  position: fixed;
  inset: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  font-family: var(--font-sans);
}
.alm__backdrop {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
}
.alm__panel {
  position: relative;
  width: 100%;
  max-width: 480px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  background: var(--surface);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-elevated, 0 10px 40px rgba(0, 0, 0, 0.2));
  overflow: hidden;
}
.alm__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 20px;
  border-bottom: 1px solid var(--outline-variant);
}
.alm__title {
  margin: 0;
  font-size: 17px;
  font-weight: 600;
  color: var(--text-primary);
}
.alm__x {
  border: 0;
  background: transparent;
  color: var(--ink-400);
  cursor: pointer;
  padding: 4px;
  border-radius: var(--radius-sm);
}
.alm__x:hover {
  color: var(--text-primary);
  background: var(--ink-100);
}
.alm__body {
  padding: 20px;
  overflow-y: auto;
}
.alm__seg {
  display: flex;
  gap: 4px;
  padding: 3px;
  background: var(--surface-variant);
  border-radius: var(--radius-md);
  margin-bottom: 16px;
}
.alm__segbtn {
  flex: 1;
  padding: 7px;
  border: 0;
  border-radius: var(--radius-sm);
  background: transparent;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  color: var(--ink-500);
}
.alm__segbtn--on {
  background: var(--surface);
  color: var(--text-primary);
  font-weight: 600;
  box-shadow: var(--hairline-border);
}
.alm__label {
  display: block;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--ink-600);
  margin: 12px 0 5px;
}
.alm__opt {
  font-weight: 400;
  color: var(--ink-400);
}
.alm__input {
  width: 100%;
  padding: 8px 10px;
  border: 1px solid var(--outline-variant);
  border-radius: var(--radius-md);
  font: inherit;
  font-size: 13.5px;
  color: var(--text-primary);
  background: var(--surface);
}
.alm__input:focus {
  outline: none;
  border-color: var(--primary);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--primary) 18%, transparent);
}
.alm__grid2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}
.alm__wt {
  display: flex;
  gap: 6px;
  margin-bottom: 8px;
  align-items: center;
}
.alm__wt-day {
  flex: 1.4;
}
.alm__wt-time {
  flex: 1;
}
.alm__wt-room {
  flex: 0.9;
}
.alm__wt-del {
  flex-shrink: 0;
  border: 0;
  background: transparent;
  color: var(--ink-400);
  cursor: pointer;
  padding: 6px;
  border-radius: var(--radius-sm);
}
.alm__wt-del:disabled {
  opacity: 0.3;
  cursor: default;
}
.alm__wt-del:not(:disabled):hover {
  color: var(--danger-ink);
  background: var(--danger-soft);
}
.alm__add {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-top: 4px;
  border: 0;
  background: transparent;
  cursor: pointer;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--primary);
}
.alm__count {
  margin-top: 16px;
  padding: 10px 12px;
  border-radius: var(--radius-md);
  background: var(--info-soft);
  color: var(--info-ink);
  font-size: 13px;
}
.alm__count b {
  font-weight: 700;
}
.alm__error {
  margin-top: 12px;
  font-size: 13px;
  color: var(--danger-ink);
}
.alm__foot {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 20px;
  border-top: 1px solid var(--outline-variant);
}
</style>
